import React, { useMemo } from "react";
import { Box, Flex } from "theme-ui";
import { Button as LumosButton, Heading } from "@loanpal/lumos__core";
import { SwitchField, Alert, Card as ReactCard } from "@aws-amplify/ui-react";

export const DEFAULT_PAYMENTS_SHARE_METHODS = {
  email: true,
  sms: false,
  online: true,
};

export function normalizePaymentsShareMethods(payments) {
  const sm = payments?.shareMethods;
  if (!sm || typeof sm !== "object") {
    return { ...DEFAULT_PAYMENTS_SHARE_METHODS };
  }
  return {
    email: sm.email !== false,
    sms: sm.sms === true,
    online: sm.online !== false,
  };
}

export function paymentsShareMethodsEqual(a, b) {
  return (
    Boolean(a?.email) === Boolean(b?.email) &&
    Boolean(a?.sms) === Boolean(b?.sms) &&
    Boolean(a?.online) === Boolean(b?.online)
  );
}

/** Payment Link Options (Online / Email / SMS). */
export function PaymentLinkOptions({
  shareMethods,
  onChangeShareMethods,
  disabled = false,
  showDescription = true,
}) {
  const setFlag = (key, value) => {
    if (disabled || !onChangeShareMethods) return;
    onChangeShareMethods({
      ...shareMethods,
      [key]: value,
    });
  };

  return (
    <Box>
      <Heading variant="h5" sx={{ mb: 2 }}>
        Create Payment Link options
      </Heading>
      {showDescription ? (
        <Box sx={{ color: "neutral.d2", fontSize: 1, mb: 3 }}>
          Choose which share methods appear on the Create Payment Link step.
          Defaults: Online and Email on, SMS off.
        </Box>
      ) : null}
      <Flex sx={{ flexDirection: "column", gap: 2, maxWidth: 360 }}>
        <SwitchField
          label="Online (Pay invoice now)"
          labelPosition="end"
          size="default"
          isDisabled={disabled}
          isChecked={Boolean(shareMethods?.online)}
          onChange={(e) => setFlag("online", e.target.checked)}
        />
        <SwitchField
          label="Email"
          labelPosition="end"
          size="default"
          isDisabled={disabled}
          isChecked={Boolean(shareMethods?.email)}
          onChange={(e) => setFlag("email", e.target.checked)}
        />
        <SwitchField
          label="SMS"
          labelPosition="end"
          size="default"
          isDisabled={disabled}
          isChecked={Boolean(shareMethods?.sms)}
          onChange={(e) => setFlag("sms", e.target.checked)}
        />
      </Flex>
    </Box>
  );
}

/**
 * Request edit — Payments settings tab.
 * Parent page runs getPaymentsConfig on load and passes gate results here.
 */
export default function RequestPaymentsTab({
  accountName,
  clientId,
  paymentsEnabled = null,
  gateLoading = false,
  gateError = "",
  shareMethods,
  onChangeShareMethods,
  onSave,
  saving,
  dirty,
  canSave = true,
}) {
  const orgLabel = String(accountName || "").trim() || "this organization";
  const cid = String(clientId || "").trim();

  const banner = useMemo(() => {
    if (!cid) {
      return {
        variation: "info",
        text: "Enter a Client ID to check whether Payments is configured for this organization.",
      };
    }
    if (gateLoading) {
      return {
        variation: "info",
        text: `Checking Payments configuration for ${orgLabel}…`,
      };
    }
    if (gateError) {
      return {
        variation: "error",
        text: `Could not verify Payments for ${orgLabel}. ${gateError}`,
      };
    }
    if (paymentsEnabled) {
      return {
        variation: "success",
        text: `Payments is configured for ${orgLabel}.`,
      };
    }
    return {
      variation: "warning",
      text: `Payments is not configured for ${orgLabel}.`,
    };
  }, [cid, gateLoading, gateError, paymentsEnabled, orgLabel]);

  return (
    <Box sx={{ mt: 0 }}>
      <Alert variation={banner.variation} hasIcon={true}>
        {banner.text}
      </Alert>

      <ReactCard style={{ marginTop: 20 }}>
        <PaymentLinkOptions
          shareMethods={shareMethods}
          onChangeShareMethods={onChangeShareMethods}
        />
        <Box sx={{ mt: 4 }}>
          <LumosButton
            type="button"
            onClick={onSave}
            disabled={!dirty || saving || !cid || !canSave || !paymentsEnabled}
          >
            {saving ? "Saving…" : "Save Payments configuration"}
          </LumosButton>
          {!canSave && cid ? (
            <Box as="span" sx={{ ml: 3, fontSize: 1, color: "neutral.d2" }}>
              Save the request first to store Payments settings
            </Box>
          ) : !dirty && cid ? (
            <Box as="span" sx={{ ml: 3, fontSize: 1, color: "neutral.d2" }}>
              No unsaved changes
            </Box>
          ) : null}
        </Box>
      </ReactCard>
    </Box>
  );
}
